import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import {
  getPaperTrades,
  getPaperMetrics,
  getPaperEquity,
  triggerPaperScan,
} from "../Service/data";

const COLORS = {
  green: "#2e7d32",
  red: "#d32f2f",
  blue: "#1976d2",
  orange: "#ed6c02",
};

const formatCurrency = (v) => {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

const formatPercent = (v) => {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(2)}%`;
};

const formatTimestamp = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString();
};

const parseScoreReasons = (str) => {
  if (!str) return [];
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch {
    return [];
  }
};

const MetricCard = ({ title, value, subtitle, color }) => (
  <Card
    sx={{
      height: "100%",
      background: "#ffffff",
      border: "1px solid #e0e0e0",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    }}
  >
    <CardContent sx={{ textAlign: "center", py: 2, "&:last-child": { pb: 2 } }}>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: color || "text.primary", mt: 0.5 }}
      >
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" display="block">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const QualityScoreDisplay = ({ score }) => {
  const s = score ?? 0;
  const circles = [];
  for (let i = 1; i <= 5; i++) {
    circles.push(
      <Box
        key={i}
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          bgcolor: i <= s ? COLORS.green : "#e0e0e0",
          display: "inline-block",
          mx: 0.25,
        }}
      />
    );
  }
  return <Box sx={{ display: "inline-flex", alignItems: "center" }}>{circles}</Box>;
};

const TradeDetailDialog = ({ trade, open, onClose }) => {
  const theme = useTheme();
  if (!trade) return null;

  const scoreReasons = parseScoreReasons(trade.score_reasons);
  const isLong = (trade.direction || "").toLowerCase() === "long";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {trade.symbol || "—"} — {(trade.direction || "").toUpperCase()}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Entry / Exit */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Entry / Exit
            </Typography>
            <Typography variant="body2">
              Entry: {formatTimestamp(trade.entry_ts)} @ {formatCurrency(trade.entry_price)}
            </Typography>
            {trade.exit_ts && (
              <Typography variant="body2">
                Exit: {formatTimestamp(trade.exit_ts)} @ {formatCurrency(trade.exit_price)}
              </Typography>
            )}
            {trade.pnl_dollar != null && (
              <Typography
                variant="body2"
                sx={{ color: trade.pnl_dollar >= 0 ? COLORS.green : COLORS.red, fontWeight: 600 }}
              >
                P&L: {formatCurrency(trade.pnl_dollar)} ({formatPercent(trade.pnl_pct)})
              </Typography>
            )}
          </Box>

          {/* Risk */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Risk
            </Typography>
            <Typography variant="body2">
              SL: {formatCurrency(trade.sl)} | TP: {formatCurrency(trade.tp)}
            </Typography>
            <Typography variant="body2">
              Risk/unit: {formatCurrency(trade.risk_per_unit)} | Size: {formatCurrency(trade.position_size)} | Risk: {formatCurrency(trade.risk_amount)}
            </Typography>
          </Box>

          {/* Strategy */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Strategy
            </Typography>
            <Typography variant="body2">
              Signal: {trade.signal_type || "—"} | Path: {trade.signal_path || "—"} | Candle: {trade.candle_type || "—"}
            </Typography>
          </Box>

          {/* Quality */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Quality Score
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <QualityScoreDisplay score={trade.quality_score} />
              <Typography variant="body2">({trade.quality_score ?? 0}/5)</Typography>
            </Box>
            {scoreReasons.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {scoreReasons.map((r, i) => (
                  <li key={i}>
                    <Typography variant="body2">{r}</Typography>
                  </li>
                ))}
              </Box>
            )}
          </Box>

          {/* Rationale */}
          {trade.rationale && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Rationale
              </Typography>
              <Typography variant="body2">{trade.rationale}</Typography>
            </Box>
          )}

          {/* Indicators */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Indicators at Entry
            </Typography>
            <Typography variant="body2">
              EMA9: {trade.ema9 != null ? Number(trade.ema9).toFixed(4) : "—"} | EMA15: {trade.ema15 != null ? Number(trade.ema15).toFixed(4) : "—"} | Slope: {trade.slope != null ? Number(trade.slope).toFixed(4) : "—"}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const PaperTradingPage = () => {
  const theme = useTheme();
  const [trades, setTrades] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [equityData, setEquityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchAll = useCallback(() => {
    Promise.all([
      getPaperTrades(),
      getPaperMetrics(),
      getPaperEquity(),
    ])
      .then(([tRes, mRes, eRes]) => {
        if (tRes?.error) setError(tRes.error);
        else {
          setTrades(tRes);
          setError(null);
        }
        if (mRes?.error) {
          // metrics can fail if no trades; use defaults
          setMetrics({
            total_trades: 0,
            closed_trades: 0,
            open_trades: 0,
            winners: 0,
            losers: 0,
            win_rate: 0,
            profit_factor: 0,
            total_pnl: 0,
            equity: 1000,
            max_drawdown_pct: 0,
            initial_capital: 1000,
          });
        } else setMetrics(mRes);
        if (eRes?.error) setEquityData({ equity_curve: [], points: 0 });
        else setEquityData(eRes);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load paper trading data.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (loading) return;
    const id = setInterval(fetchAll, 60000);
    return () => clearInterval(id);
  }, [loading, fetchAll]);

  const handleRefresh = () => {
    setLoading(true);
    fetchAll();
  };

  const handleRunScan = () => {
    setScanning(true);
    triggerPaperScan()
      .then((res) => {
        if (res?.error) {
          setError(res.error);
        } else {
          fetchAll();
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Scan failed.");
      })
      .finally(() => setScanning(false));
  };

  const openTrades = useMemo(() => {
    if (!trades?.trades) return [];
    return trades.trades.filter((t) => t.status === "open");
  }, [trades]);

  const closedTrades = useMemo(() => {
    if (!trades?.trades) return [];
    return trades.trades.filter((t) => t.status === "closed");
  }, [trades]);

  const handleRowClick = (trade) => {
    setSelectedTrade(trade);
    setDetailOpen(true);
  };

  const getQualityColor = (score) => {
    const s = score ?? 0;
    if (s >= 4) return COLORS.green;
    if (s === 3) return COLORS.orange;
    return COLORS.red;
  };

  const getExitReasonColor = (reason) => {
    if (!reason) return "default";
    const r = (reason || "").toLowerCase();
    if (r.includes("take_profit") || r === "tp") return COLORS.green;
    if (r.includes("stop_loss") || r === "sl") return COLORS.red;
    return COLORS.orange;
  };

  const openTableColumns = [
    {
      accessorKey: "symbol",
      header: "Symbol",
      size: 100,
    },
    {
      accessorKey: "direction",
      header: "Direction",
      size: 90,
      Cell: ({ cell }) => {
        const d = (cell.getValue() || "").toLowerCase();
        return (
          <Chip
            label={d}
            size="small"
            sx={{
              bgcolor: d === "long" ? "rgba(46,125,50,0.15)" : "rgba(211,47,47,0.15)",
              color: d === "long" ? COLORS.green : COLORS.red,
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      accessorKey: "entry_ts",
      header: "Entry Time",
      Cell: ({ cell }) => formatTimestamp(cell.getValue()),
    },
    {
      accessorKey: "entry_price",
      header: "Entry Price",
      Cell: ({ cell }) => formatCurrency(cell.getValue()),
    },
    {
      accessorKey: "sl",
      header: "SL",
      Cell: ({ cell }) => formatCurrency(cell.getValue()),
    },
    {
      accessorKey: "tp",
      header: "TP",
      Cell: ({ cell }) => formatCurrency(cell.getValue()),
    },
    {
      accessorKey: "quality_score",
      header: "Quality",
      size: 80,
      Cell: ({ cell }) => {
        const s = cell.getValue();
        return (
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: getQualityColor(s) }}
          >
            {s != null ? `${s}/5` : "—"}
          </Typography>
        );
      },
    },
    {
      accessorKey: "signal_path",
      header: "Signal Path",
      size: 100,
    },
    {
      accessorKey: "hold_bars",
      header: "Hold Bars",
      size: 90,
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 80,
    },
  ];

  const closedTableColumns = [
    {
      accessorKey: "symbol",
      header: "Symbol",
      size: 100,
    },
    {
      accessorKey: "direction",
      header: "Direction",
      size: 90,
      Cell: ({ cell }) => {
        const d = (cell.getValue() || "").toLowerCase();
        return (
          <Chip
            label={d}
            size="small"
            sx={{
              bgcolor: d === "long" ? "rgba(46,125,50,0.15)" : "rgba(211,47,47,0.15)",
              color: d === "long" ? COLORS.green : COLORS.red,
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      accessorKey: "entry_ts",
      header: "Entry Time",
      Cell: ({ cell }) => formatTimestamp(cell.getValue()),
    },
    {
      accessorKey: "exit_ts",
      header: "Exit Time",
      Cell: ({ cell }) => formatTimestamp(cell.getValue()),
    },
    {
      accessorKey: "entry_price",
      header: "Entry",
      Cell: ({ cell }) => formatCurrency(cell.getValue()),
    },
    {
      accessorKey: "exit_price",
      header: "Exit",
      Cell: ({ cell }) => formatCurrency(cell.getValue()),
    },
    {
      accessorKey: "pnl_pct",
      header: "P&L %",
      Cell: ({ cell }) => {
        const v = cell.getValue();
        if (v == null) return "—";
        const color = v >= 0 ? COLORS.green : COLORS.red;
        return (
          <Typography variant="body2" sx={{ fontWeight: 600, color }}>
            {formatPercent(v)}
          </Typography>
        );
      },
    },
    {
      accessorKey: "pnl_dollar",
      header: "P&L $",
      Cell: ({ cell }) => {
        const v = cell.getValue();
        if (v == null) return "—";
        const color = v >= 0 ? COLORS.green : COLORS.red;
        return (
          <Typography variant="body2" sx={{ fontWeight: 600, color }}>
            {formatCurrency(v)}
          </Typography>
        );
      },
    },
    {
      accessorKey: "hold_bars",
      header: "Hold Bars",
      size: 90,
    },
    {
      accessorKey: "exit_reason",
      header: "Exit Reason",
      Cell: ({ cell }) => {
        const r = cell.getValue();
        if (!r) return "—";
        return (
          <Chip
            label={r}
            size="small"
            sx={{
              bgcolor: `${getExitReasonColor(r)}20`,
              color: getExitReasonColor(r),
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      accessorKey: "quality_score",
      header: "Quality",
      size: 80,
      Cell: ({ cell }) => {
        const s = cell.getValue();
        return (
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: getQualityColor(s) }}
          >
            {s != null ? `${s}/5` : "—"}
          </Typography>
        );
      },
    },
    {
      accessorKey: "signal_path",
      header: "Signal Path",
      size: 100,
    },
  ];

  const openTable = useMaterialReactTable({
    columns: openTableColumns,
    data: openTrades,
    enableRowSelection: false,
    enablePagination: false,
    enableSorting: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleRowClick(row.original),
      sx: { cursor: "pointer" },
    }),
    muiTablePaperProps: {
      sx: {
        border: "1px solid #e0e0e0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      },
    },
  });

  const closedTable = useMaterialReactTable({
    columns: closedTableColumns,
    data: closedTrades,
    enableRowSelection: false,
    enablePagination: true,
    paginationDisplayMode: "pages",
    muiPaginationProps: { rowsPerPageOptions: [10, 25, 50] },
    enableSorting: true,
    initialState: {
      sorting: [{ id: "exit_ts", desc: true }],
      pagination: { pageSize: 25 },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleRowClick(row.original),
      sx: { cursor: "pointer" },
    }),
    muiTablePaperProps: {
      sx: {
        border: "1px solid #e0e0e0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      },
    },
  });

  const equityChartOptions = useMemo(() => {
    const curve = equityData?.equity_curve ?? [];
    const initial = metrics?.initial_capital ?? 1000;

    const seriesData = curve.length
      ? curve.map((p) => [new Date(p.timestamp).getTime(), p.equity])
      : [[Date.now(), initial]];

    if (curve.length && initial !== curve[0]?.equity) {
      seriesData.unshift([new Date(curve[0].timestamp).getTime() - 1, initial]);
    }

    return {
      chart: {
        type: "area",
        backgroundColor: "#ffffff",
        borderColor: "#e0e0e0",
        borderWidth: 1,
        height: 320,
      },
      title: { text: null },
      xAxis: {
        type: "datetime",
        gridLineColor: "#e0e0e0",
        lineColor: "#e0e0e0",
        tickColor: "#e0e0e0",
      },
      yAxis: {
        title: { text: "Equity ($)" },
        gridLineColor: "#e8e8e8",
        lineColor: "#e0e0e0",
        tickColor: "#e0e0e0",
      },
      plotOptions: {
        area: {
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, "rgba(46,125,50,0.4)"],
              [1, "rgba(46,125,50,0.05)"],
            ],
          },
          lineColor: COLORS.green,
          marker: { enabled: false },
        },
      },
      series: [{ name: "Equity", data: seriesData }],
      credits: { enabled: false },
      legend: { enabled: false },
    };
  }, [equityData, metrics?.initial_capital]);

  if (loading && !trades && !metrics) {
    return (
      <Box
        sx={{
          width: "100%",
          minWidth: "100vw",
          minHeight: "100vh",
          background: theme.palette.background.default,
          p: 3,
        }}
      >
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={6} sm={4} md={2} key={i}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={320} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={200} />
      </Box>
    );
  }

  const m = metrics || {};
  const totalPnl = m.total_pnl ?? 0;
  const equity = m.equity ?? m.initial_capital ?? 1000;

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: "100vw",
        minHeight: "100vh",
        background: theme.palette.background.default,
        p: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Paper Trading
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleRunScan}
            disabled={scanning}
          >
            {scanning ? "Running..." : "Run Scan"}
          </Button>
        </Box>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* Overview Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Equity"
            value={formatCurrency(equity)}
            color={equity >= (m.initial_capital ?? 1000) ? COLORS.green : COLORS.red}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total P&L"
            value={formatCurrency(totalPnl)}
            color={totalPnl >= 0 ? COLORS.green : COLORS.red}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Win Rate"
            value={`${m.win_rate ?? 0}%`}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Trades"
            value={m.total_trades ?? 0}
            subtitle={`${m.open_trades ?? 0} open / ${m.closed_trades ?? 0} closed`}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Profit Factor"
            value={m.profit_factor ?? 0}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Max Drawdown"
            value={`${m.max_drawdown_pct ?? 0}%`}
            color={COLORS.red}
          />
        </Grid>
      </Grid>

      {/* Equity Curve */}
      <Card
        sx={{
          mb: 3,
          p: 2,
          background: "#ffffff",
          border: "1px solid #e0e0e0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Equity Curve
        </Typography>
        {equityData?.equity_curve?.length > 0 ? (
          <HighchartsReact highcharts={Highcharts} options={equityChartOptions} />
        ) : (
          <Box
            sx={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#fafafa",
              borderRadius: 1,
              border: "1px dashed #e0e0e0",
            }}
          >
            <Typography color="text.secondary">
              No closed trades yet. Equity curve will appear after trades are closed.
            </Typography>
          </Box>
        )}
      </Card>

      {/* Open Trades */}
      <Card
        sx={{
          mb: 3,
          p: 2,
          background: "#ffffff",
          border: "1px solid #e0e0e0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Open Trades
        </Typography>
        {openTrades.length > 0 ? (
          <MaterialReactTable table={openTable} />
        ) : (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              color: "text.secondary",
              border: "1px dashed #e0e0e0",
              borderRadius: 1,
              bgcolor: "#fafafa",
            }}
          >
            <Typography>No open trades.</Typography>
          </Box>
        )}
      </Card>

      {/* Closed Trades */}
      <Card
        sx={{
          mb: 3,
          p: 2,
          background: "#ffffff",
          border: "1px solid #e0e0e0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Closed Trades
        </Typography>
        {closedTrades.length > 0 ? (
          <MaterialReactTable table={closedTable} />
        ) : (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              color: "text.secondary",
              border: "1px dashed #e0e0e0",
              borderRadius: 1,
              bgcolor: "#fafafa",
            }}
          >
            <Typography>No closed trades yet.</Typography>
          </Box>
        )}
      </Card>

      <TradeDetailDialog
        trade={selectedTrade}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </Box>
  );
};

export default PaperTradingPage;
